import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBranches, createBranch, deleteBranch, updateBranch, clearBranchError } from "../../redux/branchSlice";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BranchManagement = () => {
  const dispatch = useDispatch();
  const { branches, loading, error } = useSelector((state) => state.branches);
  const [newBranchName, setNewBranchName] = useState("");
  const [editingBranchId, setEditingBranchId] = useState(null);
  const [editedBranchName, setEditedBranchName] = useState("");
  const handleEditClick = (branch) => {
    setEditingBranchId(branch._id);
    setEditedBranchName(branch.name);
  };

  const handleUpdateBranch = () => {
    if (!editedBranchName.trim()) return;
    dispatch(updateBranch({ id: editingBranchId, name: editedBranchName }))
      .unwrap()
      .then(() => {
        toast.success("Branch updated.");
        setEditingBranchId(null);
        setEditedBranchName("");
        dispatch(getBranches());
      })
      .catch((err) => {
        const message =
          err?.response?.data?.message ||
          (typeof err === "string" ? err : err?.message) ||
          "Branch update failed.";
        toast.error(message);
      });
  };

  useEffect(() => {
    dispatch(getBranches());
  }, [dispatch]);

  const handleAddBranch = () => {
    const trimmedName = newBranchName.trim();
    console.log("Branch add click:", trimmedName);
    if (!trimmedName) return;

    const alreadyExists = branches.some(
      (branch) => branch.name?.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (alreadyExists) {
      console.log("Branch duplicate detected on client");
      toast.error("Branch already exists.");
      return;
    }

    dispatch(createBranch({ name: newBranchName }))
      .unwrap()
      .then(() => {
        console.log("Branch added successfully");
        toast.success("Branch added.");
        setNewBranchName("");
        dispatch(getBranches());
      })
      .catch((err) => {
        console.error("Branch create error:", err);
        const message =
          err?.response?.data?.message ||
          (typeof err === "string" ? err : err?.message) ||
          "Branch creation failed.";
        toast.error(message);
      });
  };

  useEffect(() => {
    if (error) {
      console.error("Branch error from slice:", error);
      toast.error(typeof error === "string" ? error : "Branch request failed.");
      dispatch(clearBranchError());
    }
  }, [error, dispatch]);

  const handleDeleteBranch = (id) => {
    dispatch(deleteBranch(id))
      .unwrap()
      .then(() => {
        toast.success("Branch deleted.");
        dispatch(getBranches());
      })
      .catch((err) => {
        toast.error(err || "Branch deletion failed.");
      });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h5"
        gutterBottom
        sx={{ fontSize: { xs: "20px", md: "24px" }, lineHeight: 1.3 }}
      >
        Branch Management
      </Typography>

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <TextField
          label="New branch name"
          value={newBranchName}
          onChange={(e) => setNewBranchName(e.target.value)}
          size="small"
        />
        <Button variant="contained" onClick={handleAddBranch} disabled={loading} type="button">
          {loading ? "Adding..." : "Add"}
        </Button>
      </Box>

      <List>
        {branches.map((branch) => (
          <ListItem key={branch._id} divider>
            {editingBranchId === branch._id ? (
              <>
                <TextField
                  value={editedBranchName}
                  onChange={(e) => setEditedBranchName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleUpdateBranch();
                  }}
                  size="small"
                  sx={{ mr: 2 }}
                />
                <Button
                  onClick={handleUpdateBranch}
                  variant="contained"
                  color="primary"
                  size="small"
                  sx={{ ml: 1 }}
                >
                  Kaydet
                </Button>
              </>
            ) : (
              <ListItemText primary={branch.name} />
            )}
            <ListItemSecondaryAction>
              <IconButton edge="end" aria-label="edit" onClick={() => handleEditClick(branch)} disabled={loading}>
                <EditIcon />
              </IconButton>
              <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteBranch(branch._id)} disabled={loading}>
                <DeleteIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
      <ToastContainer position="top-right" autoClose={3000} />
    </Box>
  );
};

export default BranchManagement;
